import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Intent } from '@blueprintjs/core';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonSave, LinkButton } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { Breadcrumbs, VerticalLinkGroup } from 'components/navigation';
import { AlertConfirm, AlertUnsavedData, NotificationInline, NotificationToaster } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import { UserAccess } from 'components/user-access';
import { HelperFunctions } from 'helpers';
import { useDirtyData } from 'hooks/useDirtyData';
import { useValidation } from 'hooks/useValidation';
import { PageRestricted } from 'pages/errors/page-restricted';
import {
    ReviewTypeDetailsStep,
    ReviewTypeSignOffStep,
    ReviewTypeTopicsStep,
    buildApiModel,
    buildDefaultReviewType,
    getDetailsValidationConfig,
    getSignOffValidationConfig,
    getTopicsValidationConfig,
    getTriggersValidationConfig,
    markTriggersSaved,
    isReturningToScheduling,
    mapReviewTypeFromApi
} from 'pages/employee-reviews/review-types/shared';
import { ReviewTypeHistory } from 'pages/employee-reviews/review-types/edit/review-type-history';
import { EmployeeReviewTypesService, EmployeeService } from 'services';

const requiredAction = "EmployeeReview:Manage";
const listingRoute = "/employee-reviews/review-types";
const badRequestCode = 400;
const noAccessCode = 403;
const loadFailedMessage = "Failed to get review type details. Please refresh the page.";

export function ReviewTypeEdit(props) {

    const { reviewTypeId } = useParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [invalidReviewType, setInvalidReviewType] = useState(false);
    const [employeeOptions, setEmployeeOptions] = useState({});
    const [reviewOptions, setReviewOptions] = useState({});
    const [reviewType, setReviewType] = useState(buildDefaultReviewType(null));
    const [originalReviewType, setOriginalReviewType] = useState(buildDefaultReviewType(null));
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const baseRoute = `${listingRoute}/${reviewTypeId}`;
    const detailsRoute = `${baseRoute}/details`;
    const historyRoute = `${baseRoute}/history`;
    const tabRoutes = [
        { location: detailsRoute, title: "Details & Schedule" },
        { location: `${baseRoute}/topics`, title: "Topics" },
        { location: `${baseRoute}/sign-off`, title: "Sign-off & Outcome levels" },
        { location: historyRoute, title: "History" }
    ];

    const [detailsValid, detailsErrors, validateDetails] = useValidation(false);
    const [topicsValid, topicsErrors, validateTopics] = useValidation(false);
    const [triggersValid, triggersErrors, validateTriggers] = useValidation(false);
    const [signOffValid, signOffErrors, validateSignOff] = useValidation(false);

    const isDataDirty = useDirtyData(originalReviewType, reviewType);
    const navigate = useNavigate();
    // Named routerLocation so it does not shadow the window.location global.
    const routerLocation = useLocation();

    // Once reviews have been completed against a review type it can no longer be
    // deleted (only disabled), and its start date is fixed while it stays scheduled.
    const hasCompletedReviews = reviewType?.completedReviewCount > 0;

    // Returning to scheduling restarts the schedule from a new start date, so the
    // field has to be editable even where completed reviews would otherwise fix it.
    const startDateLocked = hasCompletedReviews && !isReturningToScheduling(reviewType, originalReviewType);

    // A start date entered while the review type was returning to scheduling stops being
    // the user's to change once the lock re-applies, so the saved date is put back rather
    // than left behind a disabled field.
    useEffect(() => {
        if (!startDateLocked) {
            return;
        }

        setReviewType((current) => {
            if (current?.schedule?.startDate === originalReviewType?.schedule?.startDate) {
                return current;
            }

            return {
                ...current,
                schedule: { ...current.schedule, startDate: originalReviewType?.schedule?.startDate }
            };
        });
    }, [startDateLocked]);

    useEffect(() => {
        function errorHandler(error) {
            const status = error?.response?.status ?? error?.status;

            if (status === badRequestCode || status === noAccessCode) {
                setInvalidReviewType(true);
            } else {
                NotificationToaster.show(Intent.DANGER, loadFailedMessage);
            }
        }

        axios.all([
            EmployeeReviewTypesService.getReviewType(reviewTypeId),
            EmployeeService.getEmployeeOptions(),
            EmployeeReviewTypesService.getEmployeeReviewOptions()
        ]).then(axios.spread(function (reviewTypeResponse, employeeOptionsResponse, reviewOptionsResponse) {
            setEmployeeOptions(employeeOptionsResponse);
            setReviewOptions(reviewOptionsResponse);

            const mappedReviewType = mapReviewTypeFromApi(reviewTypeResponse, employeeOptionsResponse, reviewOptionsResponse);
            setReviewType(mappedReviewType);
            setOriginalReviewType(HelperFunctions.deepClone(mappedReviewType));
        }), errorHandler).catch((error) => {
            errorHandler(error);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (reviewType != null) {
            const details = getDetailsValidationConfig(reviewType, reviewOptions, originalReviewType);
            validateDetails(details.rules, reviewType, details.externalRules);

            const topics = getTopicsValidationConfig(reviewType, reviewOptions);
            validateTopics(topics.rules, reviewType, topics.externalRules);

            const triggers = getTriggersValidationConfig(reviewType, reviewOptions);
            validateTriggers(triggers.rules, reviewType, triggers.externalRules);

            const signOff = getSignOffValidationConfig(reviewType, reviewOptions);
            validateSignOff(signOff.rules, reviewType, signOff.externalRules);
        }
    }, [reviewType, reviewOptions, originalReviewType]);

    function onReviewTypeUpdate(newReviewType) {
        setReviewType(newReviewType);
    }

    function handleSaveError(error) {
        if (error?.status === badRequestCode) {
            NotificationToaster.show(Intent.WARNING, error.data);
        } else {
            NotificationToaster.show(Intent.DANGER, "Failed to update review type. Please try again.");
        }
    }

    function applySavedReviewType(savedReviewType) {
        setReviewType(savedReviewType);
        setOriginalReviewType(HelperFunctions.deepClone(savedReviewType));
    }

    // The save stores each changed trigger as a new one, so the review type is read back to
    // show what was stored, including when each trigger was added. If that read fails, the
    // editor keeps what it saved and asks for a refresh, as a failed load does.
    function reloadSavedReviewType() {
        return EmployeeReviewTypesService.getReviewType(reviewTypeId).then(function (reviewTypeResponse) {
            applySavedReviewType(mapReviewTypeFromApi(reviewTypeResponse, employeeOptions, reviewOptions));
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, loadFailedMessage);
        });
    }

    function onSave() {
        const model = buildApiModel(reviewType, reviewOptions);

        setSaving(true);

        EmployeeReviewTypesService.updateReviewType(model, reviewType.reviewTypeId).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Review type has been successfully updated.");
            applySavedReviewType(markTriggersSaved(reviewType));
            return reloadSavedReviewType();
        }, handleSaveError).catch((error) => {
            handleSaveError(error);
        }).finally(() => {
            setSaving(false);
        });
    }

    function onDelete() {
        setShowDeleteConfirm(true);
    }

    function onDeleteCancel() {
        setShowDeleteConfirm(false);
    }

    function onDeleteConfirm() {
        setSaving(true);

        function errorHandler() {
            NotificationToaster.show(Intent.DANGER, "Failed to delete review type. Please try again.");
        }

        EmployeeReviewTypesService.deleteReviewType(reviewType.reviewTypeId).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Review type has been successfully deleted.");

            // Allow us to navigate away with no dirty data.
            setReviewType(originalReviewType);
            // Redirect at the end of the render cycle.
            setTimeout(() => {
                navigate(listingRoute);
            }, 100);
        }, errorHandler).catch(errorHandler).finally(() => {
            setShowDeleteConfirm(false);
            setSaving(false);
        });
    }

    function renderDeleteButton() {
        return (
            <Button
                id="delete-review-type"
                icon="trash"
                text="Delete"
                intent="danger"
                loading={loading}
                disabled={saving || hasCompletedReviews}
                onClick={onDelete}
            />
        );
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <ShowHide
                    evaluator={invalidReviewType}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={props.breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="This review type does not exist, or you do not have permission to view it"
                                intent="danger"
                            />
                            <div>
                                <LinkButton intent="primary" text="Back to Review Types" id="listing-review-types" href={listingRoute} />
                            </div>
                        </div>
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={props.breadcrumbs} />
                                <h1>Edit Review Type</h1>
                            </div>
                            <div className="layout-cols">
                                <div className="col side-col">
                                    <div className="row">
                                        <VerticalLinkGroup links={tabRoutes} />
                                    </div>
                                </div>
                                <div className="col content-col">
                                    <div className="row">
                                    <Routes>
                                        <Route path="details" element={(
                                            <ReviewTypeDetailsStep
                                                reviewType={reviewType}
                                                onUpdate={onReviewTypeUpdate}
                                                errors={detailsErrors}
                                                employeeOptions={employeeOptions}
                                                reviewOptions={reviewOptions}
                                                loading={loading}
                                                saving={saving}
                                                startDateLocked={startDateLocked}
                                                originalReviewType={originalReviewType}
                                            />
                                        )} />
                                        <Route path="topics" element={(
                                            <ReviewTypeTopicsStep
                                                reviewType={reviewType}
                                                onUpdate={onReviewTypeUpdate}
                                                errors={topicsErrors}
                                                triggerErrors={triggersErrors}
                                                reviewOptions={reviewOptions}
                                                loading={loading}
                                                saving={saving}
                                            />
                                        )} />
                                        <Route path="sign-off" element={(
                                            <ReviewTypeSignOffStep
                                                reviewType={reviewType}
                                                onUpdate={onReviewTypeUpdate}
                                                errors={signOffErrors}
                                                reviewOptions={reviewOptions}
                                                loading={loading}
                                                saving={saving}
                                            />
                                        )} />
                                        <Route path="history" element={<ReviewTypeHistory reviewTypeId={reviewTypeId} />} />
                                        <Route path="" element={<Navigate to={`${baseRoute}/details`} replace />} />
                                    </Routes>
                                    {/* The History tab is read-only, so it carries no save or delete action. */}
                                    <ShowHide
                                        evaluator={routerLocation.pathname !== historyRoute}
                                        show={(
                                            <div className="button-row">
                                                <ButtonSave
                                                    id="save-review-type"
                                                    loading={loading}
                                                    disabled={saving}
                                                    simpleDisabled={!detailsValid || !topicsValid || !triggersValid || !signOffValid}
                                                    onClick={onSave}
                                                />
                                                {/* Delete only belongs on the Details & Schedule tab. */}
                                                <ShowHide
                                                    evaluator={routerLocation.pathname === detailsRoute}
                                                    show={(
                                                        <ShowHide
                                                            evaluator={hasCompletedReviews}
                                                            show={(
                                                                <Tooltip content="This review type has completed reviews. Disable it instead of deleting.">
                                                                    <span>{renderDeleteButton()}</span>
                                                                </Tooltip>
                                                            )}
                                                            hide={renderDeleteButton()}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        )}
                                    />
                                    </div>
                                </div>
                            </div>
                            <AlertUnsavedData
                                isDirty={isDataDirty}
                            />
                            <AlertConfirm
                                title="Please confirm"
                                isOpen={showDeleteConfirm}
                                onConfirm={onDeleteConfirm}
                                onCancel={onDeleteCancel}
                                confirmButtonText="Delete"
                                saving={saving}
                            >
                                <p>Are you sure you want to delete this review type?</p>
                            </AlertConfirm>
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

ReviewTypeEdit.propTypes = {
    breadcrumbs: PropTypes.array
};
